import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { RadioListOption } from 'features/charts-v2/components/RadioListOption'
import {
  POPOVER_SHADOW,
  useFixedPopoverPlacement,
} from 'features/charts-v2/hooks/use-popover-placement'
import {
  NUMERIC_OPERATOR_OPTIONS,
  type NumericOperatorType,
} from 'features/charts-v2/types'
import { useEscapeDismiss } from 'hooks/use-escape-dismiss'

interface NumericOperatorPopoverProps {
  onOperatorSelect: (operator: NumericOperatorType) => void
  onClose: () => void
  selectedOperator: NumericOperatorType
  triggerRef?: React.RefObject<HTMLElement>
  cardRef?: React.RefObject<HTMLElement>
}

export const NumericOperatorPopover: React.FC<NumericOperatorPopoverProps> = ({
  onOperatorSelect,
  onClose,
  selectedOperator,
  triggerRef,
  cardRef,
}) => {
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const highlightedItemRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const { placement, isMeasured, style } = useFixedPopoverPlacement({
    anchorRef: cardRef,
    popoverRef,
  })

  // Auto-focus the popover when it opens
  useEffect(() => {
    if (popoverRef.current) {
      popoverRef.current.focus()
    }
  }, [])

  useEscapeDismiss({ onClose })

  // Handle click outside to close popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      const isInsidePopover = popoverRef.current?.contains(target)
      const isInsideTrigger = triggerRef?.current?.contains(target)
      const isInsideCard = cardRef?.current?.contains(target)

      if (!isInsidePopover && !isInsideTrigger && !isInsideCard) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [onClose, triggerRef, cardRef])

  // Create a flat list of navigable items for keyboard navigation
  const navigableItems = useMemo(() => {
    return NUMERIC_OPERATOR_OPTIONS
  }, [])

  // Reset highlighted index when navigable items change
  useEffect(() => {
    setHighlightedIndex(navigableItems.length > 0 ? 0 : -1)
  }, [navigableItems.length])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedItemRef.current && highlightedIndex >= 0) {
      highlightedItemRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'nearest',
        inline: 'nearest',
      })
    }
  }, [highlightedIndex])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (navigableItems.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        setHighlightedIndex((prev) =>
          prev < navigableItems.length - 1 ? prev + 1 : prev,
        )
        break
      case 'ArrowUp':
        event.preventDefault()
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case 'Enter':
        event.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < navigableItems.length) {
          const item = navigableItems[highlightedIndex]
          if (item) handleSelectOperator(item.value)
        }
        break
      // Escape is handled by useEscapeDismiss above — it needs to work
      // regardless of whether this popover holds DOM focus, so it isn't
      // duplicated here.
    }
  }

  const handleSelectOperator = (operator: NumericOperatorType): void => {
    onOperatorSelect(operator)
    onClose()
  }

  const isOperatorSelected = (operator: NumericOperatorType): boolean => {
    return selectedOperator === operator
  }

  return createPortal(
    <div
      ref={popoverRef}
      data-yogi-overlay="true"
      onKeyDown={handleKeyDown}
      tabIndex={0}
      style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        boxShadow: POPOVER_SHADOW[placement],
        zIndex: 9999,
        maxHeight: 400,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        outline: 'none',
        visibility: isMeasured ? 'visible' : 'hidden',
        ...style,
      }}
    >
      {/* Options List */}
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '12px',
          minHeight: 0,
        }}
      >
        {NUMERIC_OPERATOR_OPTIONS.map((option, index) => {
          const isSelected = isOperatorSelected(option.value)
          const isHighlighted = index === highlightedIndex

          return (
            <RadioListOption
              key={option.value}
              ref={isHighlighted ? highlightedItemRef : null}
              label={option.label}
              isSelected={isSelected}
              isHighlighted={isHighlighted}
              onClick={() => handleSelectOperator(option.value)}
            />
          )
        })}
      </div>
    </div>,
    document.body,
  )
}
